/**
 * How the Libraries page groups libraries when pinning reorders it.
 *
 * Lives in `lib/utils` rather than under `pages/` because `LibraryManager` is the primary
 * consumer, and a store importing from `pages` is the edge `Tracking.ts` goes out of its way
 * to avoid. Nothing here knows about React or MobX, which is also what keeps it testable.
 *
 * Sections are identified by a semantic id, never by their display string — the copy belongs
 * to the component that renders it (`LibrarySectionLabel`), so the store can derive the
 * grouping without emitting UI text.
 */

/** `null` means "one run, draw no header". */
export type LibrarySectionId = 'pinned' | 'other';

export type LibrarySection<T> = { id: LibrarySectionId | null; items: T[] };

/**
 * Splits items into pinned and everything else, preserving the order each item arrived in.
 *
 * One pass into two buckets, so both blocks keep whatever sort the caller had already applied.
 * That is the whole contract: pinning is an *ordering* rule layered on top of the active sort,
 * never a re-sort of its own and never a membership filter.
 */
export function partitionPinned<T>(
  items: T[],
  isPinned: (item: T) => boolean,
): { pinned: T[]; rest: T[] } {
  const pinned: T[] = [];
  const rest: T[] = [];

  for (const item of items) {
    if (isPinned(item)) pinned.push(item);
    else rest.push(item);
  }

  return { pinned, rest };
}

/**
 * Groups an already-filtered, already-sorted list into the sections a Libraries surface draws.
 * Generic over the item so it stays testable without stores.
 *
 * This is the *single* derivation of the order: the flat list every surface renders is these
 * sections concatenated (`LibraryManager.sortedAndFilteredLibraries`), so a label can never
 * disagree with the sequence beneath it. It used to be derived twice — hoisted in the store,
 * then re-split in each of three components — which made that agreement a property to be
 * unit-tested rather than a fact.
 *
 * Every "when is there a header" rule lives here rather than in the surfaces, because three
 * copies of this conditional is exactly how the card grid, the mobile list and the desktop
 * table would drift apart:
 *
 *  - **`pinnedFirst` off** → one unlabelled section. The headers exist to explain the lift; with
 *    no lift there is nothing to explain. `isPinned` is never consulted, so a caller that reads
 *    it from an observable does not subscribe while the preference is off.
 *  - **Nothing pinned** → one unlabelled section. A lone "other" header over the whole page is
 *    noise.
 *  - **Everything pinned** → just the pinned section. The omnifilter can empty either bucket,
 *    and a header over zero items reads as a rendering bug.
 *  - **Otherwise** → both, pinned first.
 */
export function buildLibrarySections<T>(
  items: T[],
  { pinnedFirst, isPinned }: { pinnedFirst: boolean; isPinned: (item: T) => boolean },
): LibrarySection<T>[] {
  if (!pinnedFirst) return [{ id: null, items }];

  const { pinned, rest } = partitionPinned(items, isPinned);

  if (pinned.length === 0) return [{ id: null, items: rest }];
  if (rest.length === 0) return [{ id: 'pinned', items: pinned }];

  return [
    { id: 'pinned', items: pinned },
    { id: 'other', items: rest },
  ];
}
